/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, extendObservable, observable } from 'mobx';
import { changePageSizeHelper } from 'src/core/utils/pagination/changePageSize';
import { defragment, SortingProps, sortMap } from 'src/core/utils/pagination/pagination';
import { createBaseStoreHouse } from 'src/luis/core/stores/baseStoreHouse';
import { LuisId } from 'src/luis/types/Resource';
import { BaseStoreHouse } from 'src/luis/types/stores/core/BaseStoreHouse';
import { PaginatedResourceStoreHouse } from 'src/luis/types/stores/core/PaginatedResourceStoreHouse';
import { ResourceStore } from 'src/luis/types/stores/core/ResourceStore';
import { SubtractStore } from 'src/luis/types/SubtractStore';

/**
 * A paginated store house to be composed by all objects that identify
 * as resources in the system.
 */
export const createPaginatedResourceStoreHouse = <T extends ResourceStore>({
    pageSize,
    totalSize,
    sortingProps
}: {
    pageSize: number;
    totalSize: number;
    sortingProps: SortingProps<T>;
}): PaginatedResourceStoreHouse<T> => {
    const baseStore = createBaseStoreHouse();
    const pages = new Map<number, Map<LuisId, T>>();

    const resourcesStore: SubtractStore<PaginatedResourceStoreHouse<T>, BaseStoreHouse> = {
        pages,
        totalSize,
        pageSize,
        activePageIndex: 0,
        get currentSize() {
            let currentSize = 0;
            for (const [, page] of this.pages) {
                currentSize += page.size;
            }

            return currentSize;
        },
        /**
         * Adds a page to the pages map using the specified index.
         *
         * @param page A map of the page items to be added.
         * @param pageIndex The index of the page to be added.
         */
        addPage(page, pageIndex) {
            if (page.size > this.pageSize) {
                throw new Error('Input size must match the page size');
            }
            sortMap(page, sortingProps);

            this.pages.set(pageIndex, page);
        },
        /**
         * Change the currently set page size.
         */
        changePageSize(newPageSize) {
            const { activePageIndex, pageInfo } = changePageSizeHelper({
                activePageIndex: this.activePageIndex,
                newPageSize,
                oldPageSize: this.pageSize,
                pages: this.pages,
                totalSize: this.totalSize
            });

            this.pageSize = newPageSize;
            this.activePageIndex = activePageIndex;

            return pageInfo;
        },

        /**
         * Changes the current active page index.
         */
        changeActivePageIndex(newActivePageIndex) {
            this.activePageIndex = newActivePageIndex;
        },

        /**
         * Gets the page at the specified index.
         *
         * @param index Index of the page to be fetched.
         */
        getPage(index) {
            return this.pages.get(index);
        },

        /**
         * Gets the currently active page based on the active page index.
         */
        get activePage() {
            return this.pages.get(this.activePageIndex);
        },

        /**
         * Gets all resources in the store.
         */
        get allPages() {
            let res: T[] = [];
            for (const page of this.pages.values()) {
                res = [...res, ...page.values()];
            }

            return res;
        },

        /**
         * Adds a resource to the first page.
         *
         * @param resource Resource to be added.
         */
        add(resource) {
            this.pages.get(0).set(resource.id, resource);
            this.totalSize++;

            return defragment({
                pages: this.pages,
                pageSize: this.pageSize,
                totalSize: this.totalSize,
                activePageIndex: this.activePageIndex,
                sortingProps
            });
        },

        /**
         * Adds array of resources to the first page.
         *
         * @param resources Resources to be added.
         */
        batchAdd(resources) {
            if (!this.pages.has(0)) {
                this.addPage(new Map(), 0);
            }
            resources.forEach(resource => {
                this.pages.get(0).set(resource.id, resource);
                this.totalSize++;
            });

            return defragment({
                pages: this.pages,
                pageSize: this.pageSize,
                totalSize: this.totalSize,
                activePageIndex: this.activePageIndex,
                sortingProps
            });
        },

        /**
         * Adds resources with the page size in consecutive pages.
         *
         * @param resources Resources to be added.
         */
        batchAddWithChunking(resources) {
            const resourcesTemp = [...resources];
            while (resourcesTemp.length > 0) {
                const newPageIndex = Math.max(...[...this.pages.keys()], -1) + 1;
                this.addPage(
                    resourcesTemp.splice(0, this.pageSize).reduce((acc, val) => acc.set(val.id, val), new Map<LuisId, T>()),
                    newPageIndex
                );
            }
        },

        /**
         * Finds the item with the given id or returns undefined
         * if not found.
         *
         * @param id The id to search for.
         * @returns The item or undefined if not found.
         */
        findById(id) {
            for (const page of this.pages.values()) {
                if (page.has(id)) {
                    return page.get(id);
                }
            }
        },

        /**
         * Removes the item identified by the given id.
         *
         * @param id The id to search the item to delete with.
         * @returns The information of the page to be refreshed.
         */
        removeById(id) {
            const deleted = removeByIdHelper(this.pages, id);
            if (deleted) {
                this.totalSize--;
            }

            return defragment({
                pages: this.pages,
                pageSize: this.pageSize,
                totalSize: this.totalSize,
                activePageIndex: this.activePageIndex,
                sortingProps
            });
        },

        /**
         * Removes the items identified by the given ids.
         *
         * @param ids The ids to search the items to delete with.
         * @returns The information of the page to be refreshed.
         */
        batchRemoveById(ids) {
            ids.map(id => {
                const deleted = removeByIdHelper(this.pages, id);
                if (deleted) {
                    this.totalSize--;
                }
            });

            return defragment({
                pages: this.pages,
                pageSize: this.pageSize,
                totalSize: this.totalSize,
                activePageIndex: this.activePageIndex,
                sortingProps
            });
        },

        /**
         * Replaces an existing item with the given item. If
         * the item does not exist, the update is ignored
         * and a false flag is returned.
         *
         * @param item The item to replace with.
         * @returns True if the update operation occurred
         * and false otherwise.
         */
        replace(item) {
            for (const page of this.pages.values()) {
                if (page.has(item.id)) {
                    page.set(item.id, item);

                    return true;
                }
            }

            return false;
        },

        /**
         * Invalidates all pages inside the store.
         */
        invalidate() {
            this.pages.forEach(page => page.clear());
            this.pages.clear();
        }
    };

    return extendObservable(
        baseStore,
        resourcesStore,
        {
            activePage: computed,
            activePageIndex: observable,
            add: action,
            batchAdd: action,
            batchAddWithChunking: action,
            addPage: action,
            batchRemoveById: action,
            changeActivePageIndex: action,
            changePageSize: action,
            currentSize: computed,
            findById: action,
            allPages: computed,
            getPage: action,
            invalidate: action,
            pages: observable,
            pageSize: observable,
            removeById: action,
            replace: action,
            totalSize: observable
        },
        { deep: false }
    );
};

/**
 * Helper function for deleting an item with a specific id from the pages map.
 * @param pages The Map of pages.
 * @param id The id of the item to be deleted.
 */
const removeByIdHelper = <Id, T>(pages: Map<number, Map<Id, T>>, id: Id) => {
    for (const page of pages.values()) {
        if (page.has(id)) {
            page.delete(id);

            return true;
        }
    }

    return false;
};
